import "./Notes.scss";
import "../../components/ModalContent/ModalComponents.scss";
import ActionBar from "../../components/ActionBar/ActionBar";
import ActionBarButton from "../../components/ActionBar/ActionBarButton";
import FlowTextInput from "../../components/FlowTextInput/FlowTextInput";
import FlowButton from "../../components/FlowButton/FlowButton";
import Modal from "../../components/Modal/Modal";
import EmptyStatePlaceholder from "../../components/EmptyStatePlaceholder/EmptyStatePlaceholder";
import RemoveNote from "./Modals/RemoveNote/RemoveNote";
import RemoveDeletedNotes from "./Modals/RemoveDeletedNotes/RemoveDeletedNotes";
import RearrangeNoteGroups from "./Modals/RearrangeNoteGroups/RearrangeNoteGroups";
import AddNoteGroup from "./Modals/AddNoteGroup/AddNoteGroup";
import EditNoteGroup from "./Modals/EditNoteGroup/EditNoteGroup";
import RemoveNoteGroup from "./Modals/RemoveNoteGroup/RemoveNoteGroup";
import TextColorPicker from "./Modals/TextColorPicker/TextColorPicker";
import EmojiPickerModal from "./Modals/EmojiPickerModal/EmojiPickerModal";
// Removed UnsavedChanges modal import
import { DEFAULT_BLUE } from "../../utils/colors";
import { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { getAllNotes } from "../../api/notes/getAll";
import useAuthStore from "../../store/authStore";
import { fetcher } from "../../utils/fetcher";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TaskList, TaskItem } from "@tiptap/extension-list";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { EmoticonReplacer } from "../../utils/emoticonExtension";
import { getTextPreview } from "../../utils/textPreview";
import { useProjectRealtime, useResync } from "../../hooks/useRealtime";
import { useAutosave } from "../../hooks/useAutosave";
import OnboardingPointer from "../../components/OnboardingPointer/OnboardingPointer";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getLinkedCounts } from "../../api/tasks/getLinkedCounts";
import { jsPDF } from "jspdf";
import { loadExo2Font } from "../../utils/pdfFonts";
import useEditorActiveFormats from "../../hooks/useEditorActiveFormats";
import { useTranslation } from "react-i18next";

// Sortable Note Item Component
function SortableNoteItem({
  note,
  selectedNote,
  handleNoteSelect,
  handleDeleteNote,
  handleRestoreNote,
  linkedCount = 0,
}) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: note._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`note-item ${selectedNote?._id === note._id ? "selected" : ""} ${!note.isActive ? "deleted" : ""}`}
      onClick={() => handleNoteSelect(note)}
    >
      <div className="note-info">
        <div className="note-title">
          {note.title}
          {linkedCount > 0 && (
            <span
              className="linked-indicator"
              title={t("notes.linkedTo", { count: linkedCount })}
            >
              <span className="material-symbols-outlined">link</span>
            </span>
          )}
        </div>
        <div className="note-meta">
          {getTextPreview(note.content || "", 120)}
        </div>
      </div>
      <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
        {note.isActive && (
          <span
            className="material-symbols-outlined drag-handle"
            {...listeners}
            style={{
              fontSize: "20px",
              cursor: "grab",
              touchAction: "none",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            drag_indicator
          </span>
        )}
        {!note.isActive ? (
          <span
            className="material-symbols-outlined restore-note-icon"
            onClick={(e) => handleRestoreNote(note._id, e)}
            style={{
              fontSize: "20px",
              cursor: "pointer",
            }}
          >
            restore_from_trash
          </span>
        ) : (
          <span
            className="material-symbols-outlined delete-note-icon"
            onClick={(e) => handleDeleteNote(note._id, e)}
            style={{
              fontSize: "20px",
              cursor: "pointer",
            }}
          >
            delete
          </span>
        )}
      </div>
    </div>
  );
}

// Empty Note Group Droppable Component
function EmptyNoteGroupDropZone({ noteGroupId }) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({
    id: `empty-${noteGroupId}`,
  });

  return (
    <div
      ref={setNodeRef}
      className={`empty-note-dropzone ${isOver ? "over" : ""}`}
      style={{
        padding: "20px",
        textAlign: "center",
        color: "var(--secondary-text-color)",
        fontSize: "12px",
        opacity: isOver ? 1 : 0.5,
        backgroundColor: isOver ? "rgba(59, 130, 246, 0.15)" : "transparent",
        transition: "all 0.2s ease",
      }}
    >
      {/* {isOver ? "Drop here" : "Drag notes here"} */}
      {isOver ? t("notes.dropHere") : t("notes.emptyGroupHint")}
    </div>
  );
}

function Notes() {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [noteGroups, setNoteGroups] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [noteTitle, setNoteTitle] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [activeNote, setActiveNote] = useState(null);
  const [linkedNoteCounts, setLinkedNoteCounts] = useState({});
  const modalRef = useRef(null);
  const searchInputRef = useRef(null);
  const lastSavedNoteData = useRef({ title: "", content: "" });
  // Revision of the note version loaded in the editor - sent with saves so the
  // server can reject them if the note has been changed on another device
  const editorNoteRevRef = useRef(null);
  const editorScrollableRef = useRef(null);
  const fileInputRef = useRef(null);
  const hasRestoredNote = useRef(false);
  const isInitialMount = useRef(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const { user, currentProject, organizations, selectedNote, setSelectedNote } =
    useAuthStore();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10, // Require 10px movement before drag starts
      },
    }),
  );

  // Read search query and selectNote from URL on mount and when search params change
  useEffect(() => {
    const searchParam = searchParams.get("search");
    const selectNoteParam = searchParams.get("selectNote");

    if (searchParam) {
      setSearchText(searchParam);
    }

    // Clear the URL parameters after reading them
    if (searchParam || selectNoteParam) {
      setTimeout(() => {
        setSearchParams({});
      }, 200);
    }
  }, [searchParams, setSearchParams]);

  const handleImageUpload = async (file) => {
    if (!selectedNote || !editor) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert(t("notes.images.tooLarge"));
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert(t("notes.images.onlyImages"));
      return;
    }

    setIsUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetcher.post(
        `/api/notes/${selectedNote._id}/upload-image`,
        formData,
      );

      if (response.success && response.image) {
        // Insert the thumbnail image into the editor with initial width
        editor
          .chain()
          .focus()
          .setImage({
            src: response.image.thumbnailUrl,
            alt: response.image.filename,
          })
          .run();
      }
    } catch (err) {
      console.error("Error uploading image:", err);
      alert(t("notes.images.uploadFailed"));
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      TaskList,
      TaskItem.configure({ nested: true }),
      TextStyle,
      Color,
      EmoticonReplacer,
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: "resizable-image",
        },
      }),
    ],
    content: "",
    editable: true,
    editorProps: {
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItem = items.find((item) => item.type.startsWith("image/"));

        if (imageItem) {
          event.preventDefault();
          const file = imageItem.getAsFile();
          if (file) {
            handleImageUpload(file);
          }
          return true;
        }
        return false;
      },
      handleDOMEvents: {
        mousedown: (view, event) => {
          const target = event.target;
          if (target.tagName === "IMG") {
            const img = target;
            const rect = img.getBoundingClientRect();
            const cornerSize = 20;

            // Check if clicking in corners
            const isBottomRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isBottomLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isTopRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY < rect.top + cornerSize;

            const isTopLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY < rect.top + cornerSize;

            if (isBottomRight || isBottomLeft || isTopRight || isTopLeft) {
              event.preventDefault();

              const startX = event.clientX;
              const startY = event.clientY;
              const startWidth = img.offsetWidth;
              const aspectRatio = img.naturalHeight / img.naturalWidth;

              const handleMouseMove = (e) => {
                const diffX = e.clientX - startX;
                const diffY = e.clientY - startY;

                // Use the larger absolute difference to maintain aspect ratio
                const diff =
                  Math.abs(diffX) > Math.abs(diffY)
                    ? diffX
                    : diffY / aspectRatio;

                // Apply direction multiplier for left corners
                const directionMultiplier = isBottomLeft || isTopLeft ? -1 : 1;

                const newWidth = Math.max(
                  100,
                  Math.min(
                    startWidth + diff * directionMultiplier,
                    view.dom.offsetWidth - 40,
                  ),
                );

                img.style.width = newWidth + "px";
                img.style.height = "auto";
              };

              const handleMouseUp = () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
                document.body.style.cursor = "";

                // Update the image node with new width
                const pos = view.posAtDOM(img, 0);
                if (pos !== null && pos !== undefined) {
                  const node = view.state.doc.nodeAt(pos);
                  if (node && node.type.name === "image") {
                    view.dispatch(
                      view.state.tr.setNodeMarkup(pos, null, {
                        ...node.attrs,
                        width: img.style.width,
                      }),
                    );
                  }
                }
              };

              // Set appropriate cursor
              if (isBottomRight || isTopLeft) {
                document.body.style.cursor = "nwse-resize";
              } else {
                document.body.style.cursor = "nesw-resize";
              }

              document.addEventListener("mousemove", handleMouseMove);
              document.addEventListener("mouseup", handleMouseUp);
            }
          }
          return false;
        },
        mousemove: (view, event) => {
          const target = event.target;
          if (target.tagName === "IMG") {
            const img = target;
            const rect = img.getBoundingClientRect();
            const cornerSize = 20;

            // Update cursor based on position
            const isBottomRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isBottomLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isTopRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY < rect.top + cornerSize;

            const isTopLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY < rect.top + cornerSize;

            if (isBottomRight || isTopLeft) {
              img.style.cursor = "nwse-resize";
            } else if (isBottomLeft || isTopRight) {
              img.style.cursor = "nesw-resize";
            } else {
              img.style.cursor = "default";
            }
          }
          return false;
        },
      },
    },
  });
  const activeFormats = useEditorActiveFormats(editor);

  // Handle selectNote URL parameter - find and select the note
  // This must be after useEditor since it uses the editor
  useEffect(() => {
    const selectNoteParam = searchParams.get("selectNote");
    if (selectNoteParam && noteGroups.length > 0) {
      // Find the note across all groups
      for (const group of noteGroups) {
        const noteToSelect = group.notes?.find(
          (note) => note._id === selectNoteParam || note.id === selectNoteParam,
        );
        if (noteToSelect) {
          performNoteSelection(noteToSelect);
          break;
        }
      }
    }
  }, [searchParams, noteGroups, editor]);

  const fetchNotes = useCallback(() => {
    // Get current project directly from the store to avoid stale closures
    const currentProj = useAuthStore.getState().currentProject;

    // Don't fetch if there's no current project or project ID
    if (!currentProj || !currentProj._id) {
      setNoteGroups([]);
      setIsInitialLoadComplete(true);
      return;
    }

    return getAllNotes(showCompleted, currentProj._id)
      .then((data) => {
        setNoteGroups(data);
        setIsInitialLoadComplete(true);
        return data;
      })
      .catch((err) => {
        console.error("Error fetching note groups:", err);
        // If it's a projectId error, clear the note groups
        if (err.message?.includes("projectId")) {
          setNoteGroups([]);
        }
        setIsInitialLoadComplete(true);
      });
  }, [showCompleted]);

  const fetchLinkedCounts = useCallback(async () => {
    const currentProj = useAuthStore.getState().currentProject;
    if (!currentProj || !currentProj._id) {
      setLinkedNoteCounts({});
      return;
    }
    try {
      const data = await getLinkedCounts(currentProj._id);
      setLinkedNoteCounts(data.noteCounts || {});
    } catch (err) {
      console.error("Error fetching linked counts:", err);
    }
  }, []);

  useEffect(() => {
    // Fetch notes whenever currentProject changes (including when it loads from localStorage)
    if (currentProject?._id) {
      fetchNotes();
      fetchLinkedCounts();
    }
  }, [currentProject?._id, showCompleted, fetchNotes, fetchLinkedCounts]);

  // Real-time WebSocket updates for notes
  useProjectRealtime(currentProject?._id, {
    onNoteCreated: () => {
      fetchNotes();
    },
    onNoteUpdated: (updatedNote) => {
      // Don't refetch on note updates - the editor already has the latest data
      // and calling fetchNotes() during autosave causes unnecessary network traffic.
      // If the open note was changed on another device, show that version
      // (unless there are unsaved changes here).
      if (selectedNote && updatedNote._id === selectedNote._id) {
        loadServerNoteIntoEditor(updatedNote);
      }
    },
    onNoteDeleted: () => {
      fetchNotes();
    },
    onNoteGroupCreated: () => {
      fetchNotes();
    },
    onNoteGroupUpdated: () => {
      fetchNotes();
    },
    onNoteGroupDeleted: () => {
      fetchNotes();
    },
  });

  // Load the server's version of the open note into the editor, unless it is
  // already shown or there are unsaved changes (a conflict discards those)
  const loadServerNoteIntoEditor = (
    serverNote,
    { discardUnsaved = false } = {},
  ) => {
    if (!editor || serverNote.rev === editorNoteRevRef.current) return;

    const hasUnsavedChanges =
      noteTitle.trim() !== lastSavedNoteData.current.title ||
      editor.getHTML() !== lastSavedNoteData.current.content;
    if (hasUnsavedChanges && !discardUnsaved) return;

    setSelectedNote(serverNote);
    setNoteTitle(serverNote.title);
    editor.commands.setContent(serverNote.content || "");
    lastSavedNoteData.current = {
      title: serverNote.title.trim(),
      content: serverNote.content || "",
    };
    editorNoteRevRef.current = serverNote.rev;
  };

  // Reload when this tab may have missed changes made on another device
  useResync((reason, conflictId) => {
    fetchLinkedCounts();
    fetchNotes()?.then((data) => {
      if (!data || !selectedNote) return;
      for (const group of data) {
        const serverNote = group.notes?.find((n) => n._id === selectedNote._id);
        if (serverNote) {
          loadServerNoteIntoEditor(serverNote, {
            discardUnsaved:
              reason === "conflict" && conflictId === selectedNote._id,
          });
          break;
        }
      }
    });
  });

  // Restore selected note content when coming back to the page (only once on mount)
  useEffect(() => {
    if (
      selectedNote &&
      editor &&
      noteGroups.length > 0 &&
      !hasRestoredNote.current
    ) {
      // Find the note in the current noteGroups to ensure it still exists
      let foundNote = null;
      for (const group of noteGroups) {
        foundNote = group.notes?.find((n) => n._id === selectedNote._id);
        if (foundNote) break;
      }

      if (foundNote) {
        // Only restore if the editor is empty or different
        const currentContent = editor.getHTML();
        if (
          !currentContent ||
          currentContent === "<p></p>" ||
          currentContent !== foundNote.content
        ) {
          setNoteTitle(foundNote.title);
          editor.commands.setContent(foundNote.content || "");
          lastSavedNoteData.current = {
            title: foundNote.title.trim(),
            content: foundNote.content || "",
          };
        }
        editorNoteRevRef.current = foundNote.rev;
        hasRestoredNote.current = true;
      } else {
        // Note no longer exists, clear selection
        setSelectedNote(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteGroups, editor]);

  // Clear selected note when project changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    setSelectedNote(null);
    hasRestoredNote.current = false;
  }, [currentProject?._id, setSelectedNote]);

  useEffect(() => {
    // Use setTimeout to ensure the component is fully rendered
    const timer = setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Auto-save once the user has paused typing
  const { scheduleSave, flushSave, isAutosaving } = useAutosave({
    enabled: autoUpdateEnabled && !!selectedNote,
    hasChanges: () =>
      !!editor &&
      (noteTitle.trim() !== lastSavedNoteData.current.title ||
        editor.getHTML() !== lastSavedNoteData.current.content),
    save: () => handleUpdateNote(true),
  });

  useEffect(() => {
    if (!editor) return;
    editor.on("update", scheduleSave);
    return () => editor.off("update", scheduleSave);
  }, [editor, scheduleSave]);

  useEffect(() => {
    scheduleSave();
  }, [noteTitle, scheduleSave]);

  const handleSearchChange = (value) => {
    setSearchText(value);
  };

  const toggleGroup = (groupName) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const collapseAll = () => {
    const newState = {};
    noteGroups.forEach((g) => {
      newState[g?.name] = true;
    });
    setCollapsedGroups(newState);
  };

  const expandAll = () => {
    const newState = {};
    noteGroups.forEach((g) => {
      newState[g?.name] = false;
    });
    setCollapsedGroups(newState);
  };

  const openModal = (e, arrow, content) => {
    const triggerElement = e.currentTarget;

    if (modalRef.current) {
      modalRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const closeModal = () => {
    if (modalRef.current) {
      modalRef.current.close();
    }
  };

  const getFilteredNoteGroups = () => {
    if (!searchText.trim()) {
      return noteGroups;
    }

    const searchLower = searchText.toLowerCase();

    return noteGroups
      .map((noteGroup) => ({
        ...noteGroup,
        notes: noteGroup.notes?.filter(
          (note) =>
            note.title.toLowerCase().includes(searchLower) ||
            note.content?.toLowerCase().includes(searchLower),
        ),
      }))
      .filter((noteGroup) => noteGroup.notes && noteGroup.notes.length > 0);
  };

  // Generate PDF for the selected note with images
  const generateNotePDF = async () => {
    if (!selectedNote) return;

    // Export what the editor shows: selectedNote isn't updated on save
    const title = noteTitle.trim() || selectedNote.title;
    const content = editor ? editor.getHTML() : selectedNote.content;

    const doc = new jsPDF();

    // Load Exo 2 font
    await loadExo2Font(doc);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let yPosition = margin;

    // Helper to check if new content fits on page
    const checkPageBreak = (requiredHeight) => {
      if (yPosition + requiredHeight > pageHeight - 20) {
        doc.addPage();
        yPosition = margin;
      }
    };

    // Title
    doc.setFontSize(18);
    doc.setFont("Exo2", "bold");
    doc.splitTextToSize(title, maxWidth).forEach((titleLine) => {
      checkPageBreak(8);
      doc.text(titleLine, margin, yPosition);
      yPosition += 8;
    });
    yPosition += 4;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont("Exo2", "normal");

    // Parse HTML content to extract text and images
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content || "";

    // Add an <img> to the PDF (scaled to fit the page width)
    const addImage = async (node) => {
      const src = node.src || node.getAttribute("src");

      if (src) {
        try {
          let imageSrc = src;
          let format = "JPEG";

          // For external URLs, use proxy to avoid CORS issues
          if (!src.startsWith("data:")) {
            try {
              // Get auth token
              let token = null;
              try {
                const authStorage = localStorage.getItem("auth-storage");
                if (authStorage) {
                  const parsedAuth = JSON.parse(authStorage);
                  token = parsedAuth.state?.token;
                }
              } catch (e) {
                console.error("Error getting auth token:", e);
              }

              // Use full backend URL (same pattern as fetcher.js)
              const baseURL =
                import.meta.env.VITE_API_URL || "http://localhost:5001";
              const proxyUrl = `${baseURL}/api/proxy-image?url=${encodeURIComponent(src)}`;
              const response = await fetch(proxyUrl, {
                headers: {
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
              });

              if (!response.ok) {
                throw new Error(`Proxy failed: ${response.status}`);
              }

              const blob = await response.blob();

              // Detect format from blob type
              if (blob.type === "image/png") {
                format = "PNG";
              } else if (
                blob.type === "image/jpeg" ||
                blob.type === "image/jpg"
              ) {
                format = "JPEG";
              } else if (blob.type === "image/gif") {
                format = "GIF";
              }

              // Convert blob to data URL
              imageSrc = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
            } catch (fetchError) {
              throw fetchError;
            }
          } else {
            // Detect format from data URL
            if (src.startsWith("data:image/png")) {
              format = "PNG";
            } else if (
              src.startsWith("data:image/jpeg") ||
              src.startsWith("data:image/jpg")
            ) {
              format = "JPEG";
            } else if (src.startsWith("data:image/gif")) {
              format = "GIF";
            }
          }

          // Calculate image dimensions
          const maxImgWidth = maxWidth;
          const maxImgHeight = 150; // Max height in PDF units

          // Load the image to get dimensions (use window.Image to avoid conflict with TipTap's Image import)
          const img = new window.Image();

          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error("Image load timeout"));
            }, 5000); // 5 second timeout

            img.onload = () => {
              clearTimeout(timeout);
              resolve();
            };
            img.onerror = (e) => {
              clearTimeout(timeout);
              reject(e);
            };
            img.src = imageSrc;
          });

          let imgWidth = img.width;
          let imgHeight = img.height;

          // Skip if image didn't load properly
          if (!imgWidth || !imgHeight) {
            throw new Error("Invalid image dimensions");
          }

          // Scale to fit within max dimensions while maintaining aspect ratio
          const widthScale = maxImgWidth / imgWidth;
          const heightScale = maxImgHeight / imgHeight;
          const scale = Math.min(widthScale, heightScale, 1); // Don't upscale

          imgWidth = imgWidth * scale;
          imgHeight = imgHeight * scale;

          // Check if image fits on current page
          checkPageBreak(imgHeight + 5);

          // Add image to PDF
          doc.addImage(
            imageSrc,
            format,
            margin,
            yPosition,
            imgWidth,
            imgHeight,
          );
          yPosition += imgHeight + 5; // Add spacing after image
        } catch (error) {
          console.error("Error adding image to PDF:", error);
          // Skip images that fail to load - don't show error message in PDF
        }
      }
    };

    // ---- Rich text rendering ----
    const PT_TO_MM = 0.3528;
    const BASE_FONT_SIZE = 11;
    const BLACK = [0, 0, 0];
    const GREY = [130, 130, 130];
    const HEADING_SIZES = { h1: 18, h2: 15, h3: 13, h4: 12, h5: 11, h6: 11 };
    const BLOCK_TAGS = new Set([
      "p",
      "div",
      "ul",
      "ol",
      "li",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "blockquote",
      "pre",
      "img",
      "hr",
      "label",
    ]);

    // CSS color ("#rrggbb", "rgb(...)") -> [r, g, b]. Very light colors (picked
    // for the dark editor) would vanish on white paper, so they become black.
    const parseColor = (value) => {
      if (!value) return null;
      let rgb;
      const hex = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
      if (hex) {
        let h = hex[1];
        if (h.length === 3) {
          h = h
            .split("")
            .map((c) => c + c)
            .join("");
        }
        rgb = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
      } else {
        const fn = value.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
        if (!fn) return null;
        rgb = fn.slice(1, 4).map(Number);
      }
      const lightness = (Math.max(...rgb) + Math.min(...rgb)) / 2 / 255;
      return lightness > 0.85 ? null : rgb;
    };

    const LINK_COLOR = [35, 130, 228];
    const CODE_COLOR = [200, 70, 80];

    // Inline content of a node as styled text runs
    // ({ text, color, bold, italic, strike, underline, link })
    const collectRuns = (node, style) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.replace(/\s+/g, " ");
        return text ? [{ ...style, text }] : [];
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return [];
      const tag = node.tagName.toLowerCase();
      if (tag === "br") return [{ ...style, text: "\n" }];
      if (BLOCK_TAGS.has(tag)) return [];

      const next = { ...style };
      if (tag === "strong" || tag === "b") next.bold = true;
      if (tag === "em" || tag === "i") next.italic = true;
      if (tag === "s" || tag === "del" || tag === "strike") next.strike = true;
      if (tag === "u") next.underline = true;
      if (tag === "code") next.color = CODE_COLOR;
      if (tag === "a" && node.getAttribute("href")) {
        next.link = node.getAttribute("href");
        next.color = LINK_COLOR;
        next.underline = true;
      }
      const color = parseColor(node.style?.color);
      if (color) next.color = color;
      return Array.from(node.childNodes).flatMap((child) =>
        collectRuns(child, next),
      );
    };

    // Runs for the inline content inside a block element (p, h1, ...)
    const inlineRuns = (block, style) =>
      Array.from(block.childNodes).flatMap((child) =>
        collectRuns(child, style),
      );

    // The PDF font has no emoji glyphs, so emojis are drawn as images
    const EMOJI_SPLIT =
      /(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic}|\p{Emoji_Modifier})*)/u;
    const isEmoji = (text) => /^\p{Extended_Pictographic}/u.test(text);
    const emojiImages = new Map();
    const emojiImage = (emoji) => {
      if (!emojiImages.has(emoji)) {
        const canvas = document.createElement("canvas");
        canvas.width = canvas.height = 64;
        const ctx = canvas.getContext("2d");
        ctx.font =
          '52px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(emoji, 32, 36);
        emojiImages.set(emoji, canvas.toDataURL("image/png"));
      }
      return emojiImages.get(emoji);
    };

    // Draw text runs from x, wrapping words at the right margin.
    // Returns false if there was no visible text.
    const renderRuns = (runs, x, fontSize = BASE_FONT_SIZE) => {
      const lineHeight = fontSize * PT_TO_MM * 1.5;
      const width = pageWidth - margin - x;
      const emojiSize = fontSize * PT_TO_MM * 1.15;
      const setRunFont = (run) =>
        doc.setFont(
          "Exo2",
          run.bold && run.italic
            ? "bolditalic"
            : run.bold
              ? "bold"
              : run.italic
                ? "italic"
                : "normal",
        );
      const measure = (token) =>
        token.emoji ? emojiSize : doc.getTextWidth(token.text);
      doc.setFontSize(fontSize);

      // Words, spaces, line breaks and emojis as separate tokens, so lines
      // can wrap in the middle of a run
      const tokens = runs.flatMap((run) =>
        run.text
          .split(/(\n| +)/)
          .filter(Boolean)
          .flatMap((text) =>
            text
              .split(EMOJI_SPLIT)
              .filter(Boolean)
              .map((part) => ({ ...run, text: part, emoji: isEmoji(part) })),
          ),
      );
      if (!tokens.some((t) => t.text.trim())) return false;

      let line = [];
      let lineWidth = 0;
      const flushLine = () => {
        while (line.length && !line[line.length - 1].text.trim()) line.pop();
        checkPageBreak(lineHeight);
        let cursorX = x;
        line.forEach((token) => {
          setRunFont(token);
          const tokenWidth = measure(token);
          if (token.emoji) {
            const image = emojiImage(token.text);
            if (image) {
              doc.addImage(
                image,
                "PNG",
                cursorX,
                yPosition - emojiSize * 0.82,
                emojiSize,
                emojiSize,
              );
            }
          } else {
            doc.setTextColor(...token.color);
            doc.text(token.text, cursorX, yPosition);
          }
          const decorate = (offset) => {
            const lineY = yPosition - fontSize * PT_TO_MM * offset;
            doc.setDrawColor(...token.color);
            doc.setLineWidth(0.2);
            doc.line(cursorX, lineY, cursorX + tokenWidth, lineY);
          };
          if (token.text.trim()) {
            if (token.strike) decorate(0.3);
            if (token.underline) decorate(-0.15);
            if (token.link) {
              doc.link(
                cursorX,
                yPosition - fontSize * PT_TO_MM * 0.8,
                tokenWidth,
                fontSize * PT_TO_MM,
                { url: token.link },
              );
            }
          }
          cursorX += tokenWidth;
        });
        yPosition += lineHeight;
        line = [];
        lineWidth = 0;
      };

      tokens.forEach((token) => {
        if (token.text === "\n") {
          flushLine();
          return;
        }
        const isSpace = !token.text.trim();
        if (isSpace && line.length === 0) return;
        setRunFont(token);
        let tokenWidth = measure(token);
        if (!isSpace && lineWidth + tokenWidth > width && line.length) {
          flushLine();
        }
        // A single word wider than the line is split over several lines
        if (!isSpace && !token.emoji && tokenWidth > width) {
          const parts = doc.splitTextToSize(token.text, width);
          parts.slice(0, -1).forEach((part) => {
            line.push({ ...token, text: part });
            flushLine();
          });
          token = { ...token, text: parts[parts.length - 1] };
          tokenWidth = doc.getTextWidth(token.text);
        }
        line.push(token);
        lineWidth += tokenWidth;
      });
      if (line.length) flushLine();

      doc.setTextColor(...BLACK);
      doc.setFont("Exo2", "normal");
      doc.setFontSize(BASE_FONT_SIZE);
      return true;
    };

    const drawCheckbox = (x, baselineY, checked) => {
      const size = 3.2;
      const top = baselineY - 2.8;
      doc.setLineWidth(0.3);
      doc.setDrawColor(...GREY);
      doc.rect(x, top, size, size);
      if (checked) {
        doc.setDrawColor(35, 130, 228);
        doc.setLineWidth(0.5);
        doc.line(x + 0.6, top + 1.7, x + 1.4, top + 2.5);
        doc.line(x + 1.4, top + 2.5, x + 2.7, top + 0.7);
      }
    };

    // Render a node's children: consecutive inline content as one paragraph,
    // block elements on their own
    const renderChildren = async (node, x, style) => {
      let inline = [];
      const flushInline = () => {
        if (inline.length) renderRuns(inline, x);
        inline = [];
      };
      for (const child of node.childNodes) {
        const isBlock =
          child.nodeType === Node.ELEMENT_NODE &&
          BLOCK_TAGS.has(child.tagName.toLowerCase());
        if (isBlock) {
          flushInline();
          await renderBlock(child, x, style);
        } else {
          inline.push(...collectRuns(child, style));
        }
      }
      flushInline();
    };

    const renderList = async (list, x, style) => {
      const isTaskList = list.getAttribute("data-type") === "taskList";
      const isOrdered = list.tagName.toLowerCase() === "ol";
      let number = parseInt(list.getAttribute("start") || "1", 10);
      const items = Array.from(list.children).filter(
        (child) => child.tagName.toLowerCase() === "li",
      );

      for (const item of items) {
        const textX = x + (isOrdered ? 7 : 6);
        checkPageBreak(BASE_FONT_SIZE * PT_TO_MM * 1.5);
        const itemStyle = { ...style, inList: true };

        if (isTaskList) {
          const checked = item.getAttribute("data-checked") === "true";
          drawCheckbox(x, yPosition, checked);
          if (checked) Object.assign(itemStyle, { color: GREY, strike: true });
        } else {
          doc.setFont("Exo2", "normal");
          doc.setTextColor(...BLACK);
          doc.text(isOrdered ? `${number}.` : "•", x, yPosition);
          number++;
        }

        // Nested lists don't inherit a checked item's strikethrough
        let inline = [];
        const flushInline = () => {
          if (inline.length) renderRuns(inline, textX);
          inline = [];
        };
        const renderItemContent = async (node) => {
          for (const child of node.childNodes) {
            const tag =
              child.nodeType === Node.ELEMENT_NODE
                ? child.tagName.toLowerCase()
                : null;
            if (tag === "label") continue; // The editor's checkbox
            if (tag === "div") {
              await renderItemContent(child); // TaskItem content wrapper
            } else if (tag === "ul" || tag === "ol") {
              flushInline();
              await renderList(child, textX, { ...style, inList: true });
            } else if (tag && BLOCK_TAGS.has(tag)) {
              flushInline();
              await renderBlock(child, textX, itemStyle);
            } else {
              inline.push(...collectRuns(child, itemStyle));
            }
          }
        };
        await renderItemContent(item);
        flushInline();
      }
    };

    const renderBlock = async (node, x, style) => {
      if (node.nodeType === Node.TEXT_NODE) {
        renderRuns(collectRuns(node, style), x);
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const tag = node.tagName.toLowerCase();

      if (tag === "img") {
        await addImage(node);
      } else if (HEADING_SIZES[tag]) {
        yPosition += 2;
        renderRuns(
          inlineRuns(node, { ...style, bold: true }),
          x,
          HEADING_SIZES[tag],
        );
        yPosition += 1;
      } else if (tag === "p") {
        // An empty paragraph is a blank line
        if (!renderRuns(inlineRuns(node, style), x)) {
          yPosition += BASE_FONT_SIZE * PT_TO_MM * 1.5;
        }
        yPosition += style.inList ? 0.5 : 2;
      } else if (tag === "ul" || tag === "ol") {
        await renderList(node, x, style);
        if (!style.inList) yPosition += 2;
      } else if (tag === "blockquote") {
        const startY = yPosition;
        const startPage = doc.getNumberOfPages();
        await renderChildren(node, x + 5, { ...style, color: GREY });
        if (doc.getNumberOfPages() === startPage) {
          doc.setDrawColor(...GREY);
          doc.setLineWidth(0.6);
          doc.line(x + 1, startY - 4, x + 1, yPosition - 4);
        }
      } else if (tag === "pre") {
        node.textContent.split("\n").forEach((codeLine) => {
          if (!renderRuns([{ ...style, color: GREY, text: codeLine }], x + 4)) {
            yPosition += BASE_FONT_SIZE * PT_TO_MM * 1.5;
          }
        });
        yPosition += 2;
      } else if (tag === "hr") {
        checkPageBreak(6);
        doc.setDrawColor(...GREY);
        doc.setLineWidth(0.3);
        doc.line(x, yPosition - 2, pageWidth - margin, yPosition - 2);
        yPosition += 4;
      } else if (tag === "br") {
        yPosition += BASE_FONT_SIZE * PT_TO_MM * 1.5;
      } else {
        await renderChildren(node, x, style);
      }
    };

    // Process all content
    const baseStyle = {
      color: BLACK,
      bold: false,
      italic: false,
      strike: false,
      underline: false,
      inList: false,
    };
    for (const child of tempDiv.childNodes) {
      await renderBlock(child, margin, baseStyle);
    }

    // Save the PDF
    const fileName = `${title.replace(/[^a-z0-9]/gi, "_")}.pdf`;
    doc.save(fileName);
  };

  const handleNoteSelect = (note) => {
    // If clicking the same note, do nothing
    if (selectedNote && note._id === selectedNote._id) {
      return;
    }
    performNoteSelection(note);
  };

  const performNoteSelection = (note) => {
    // Save pending changes to the previous note before switching
    flushSave();

    hasRestoredNote.current = true; // Mark as restored since we're manually selecting
    setSelectedNote(note);
    setNoteTitle(note.title);

    // Set baseline for auto-update comparison
    lastSavedNoteData.current = {
      title: note.title.trim(),
      content: note.content || "",
    };
    editorNoteRevRef.current = note.rev;

    if (editor) {
      editor.commands.setContent(note.content || "");
      // Focus at the start of the content (top)
      editor.commands.focus("start");
    }

    // Scroll editor container to top
    if (editorScrollableRef.current) {
      editorScrollableRef.current.scrollTop = 0;
    }
  };

  const handleAddNote = async (noteGroupId, title) => {
    if (!title.trim()) return;

    try {
      const newNote = await fetcher.post("/api/notes", {
        groupId: noteGroupId,
        title: title.trim(),
        content: "",
        color: DEFAULT_BLUE, // Default color
        isActive: true,
      });

      // Refresh notes
      await fetchNotes();

      // Select the new note and focus on the editor
      setSelectedNote(newNote);
      editorNoteRevRef.current = newNote.rev;
      setNoteTitle(title.trim());
      if (editor) {
        editor.commands.setContent("");
        editor.commands.focus();
      }
    } catch (err) {
      console.error("Error creating note:", err);
    }
  };

  const handleUpdateNote = useCallback(
    async (isBackground = false) => {
      if (!selectedNote || !editor) return;

      const updatedTitle = noteTitle.trim();
      const updatedContent = editor.getHTML();

      if (isBackground) {
        // Background save: no waiting, errors are only logged
        return fetcher
          .put(`/api/notes/${selectedNote._id}`, {
            rev: editorNoteRevRef.current,
            title: noteTitle,
            content: updatedContent,
          })
          .then((savedNote) => {
            editorNoteRevRef.current = savedNote.rev;

            // Update baseline after successful save
            lastSavedNoteData.current = {
              title: updatedTitle,
              content: updatedContent,
            };

            // Update the note in local noteGroups state to avoid stale data
            setNoteGroups((prev) =>
              prev.map((group) => ({
                ...group,
                notes: group.notes?.map((note) =>
                  note._id === selectedNote._id
                    ? { ...note, title: noteTitle, content: updatedContent }
                    : note,
                ),
              })),
            );
          })
          .catch((err) => {
            console.error("Error updating note (background):", err);
          });
      }

      // Manual save: show feedback and wait for response
      setIsUploading(true);

      // Ensure the spin animation runs for at least 1 second
      const minimumSpinTime = new Promise((resolve) =>
        setTimeout(resolve, 1000),
      );

      try {
        const savedNote = await fetcher.put(`/api/notes/${selectedNote._id}`, {
          rev: editorNoteRevRef.current,
          title: noteTitle,
          content: updatedContent,
        });
        editorNoteRevRef.current = savedNote.rev;

        // Update baseline after successful save
        lastSavedNoteData.current = {
          title: updatedTitle,
          content: updatedContent,
        };

        // Update the note in local noteGroups state to avoid stale data
        setNoteGroups((prev) =>
          prev.map((group) => ({
            ...group,
            notes: group.notes?.map((note) =>
              note._id === selectedNote._id
                ? { ...note, title: noteTitle, content: updatedContent }
                : note,
            ),
          })),
        );

        // Don't call fetchNotes() here - it causes the editor to lose focus during typing
        // The WebSocket will handle real-time updates from other clients
        // Manual updates (button click) will refresh via separate flow if needed
      } catch (err) {
        console.error("Error updating note:", err);
      } finally {
        // Wait for the minimum spin time before stopping the animation
        await minimumSpinTime;
        setIsUploading(false);
      }
    },
    [selectedNote, editor, noteTitle],
  );

  const handleDeleteNote = (noteId, e) => {
    e.stopPropagation(); // Prevent note selection when clicking delete

    openModal(
      e,
      "center-center",
      <RemoveNote
        removeNote={() => confirmDeleteNote(noteId)}
        cancelRemoveNote={closeModal}
      />,
    );
  };

  const confirmDeleteNote = async (noteId) => {
    try {
      await fetcher.put(`/api/notes/${noteId}`, {
        isActive: false,
      });

      // If the deleted note was selected, clear the selection
      if (selectedNote?._id === noteId) {
        setSelectedNote(null);
        setNoteTitle("");
        if (editor) {
          editor.commands.setContent("");
        }
      }

      // Refresh notes
      await fetchNotes();
      closeModal();
    } catch (err) {
      console.error("Error deleting note:", err);
    }
  };

  const handleRestoreNote = async (noteId, e) => {
    e.stopPropagation(); // Prevent note selection when clicking restore

    try {
      await fetcher.put(`/api/notes/${noteId}`, {
        isActive: true,
      });

      // Refresh notes
      await fetchNotes();
    } catch (err) {
      console.error("Error restoring note:", err);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    const { active } = event;

    // Find the note being dragged
    for (const group of noteGroups) {
      const note = group.notes?.find((n) => n._id === active.id);
      if (note) {
        setActiveNote(note);
        break;
      }
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Check if dropping on an empty note group
    const isEmptyDropZone = over.id.toString().startsWith("empty-");

    // Find source note and group
    let sourceGroup = null;
    let sourceNoteIndex = -1;

    for (const group of noteGroups) {
      const noteIndex =
        group.notes?.findIndex((n) => n._id === active.id) ?? -1;
      if (noteIndex !== -1) {
        sourceGroup = group;
        sourceNoteIndex = noteIndex;
        break;
      }
    }

    if (!sourceGroup) {
      return;
    }

    // Handle dropping on empty note group
    if (isEmptyDropZone) {
      const targetGroupId = over.id.toString().replace("empty-", "");
      const targetGroup = noteGroups.find((g) => g._id === targetGroupId);

      if (!targetGroup || sourceGroup._id === targetGroup._id) {
        return;
      }

      const newNoteGroups = noteGroups.map((g) => ({
        ...g,
        notes: [...(g.notes || [])],
      }));

      const sourceGroupIndex = newNoteGroups.findIndex(
        (g) => g._id === sourceGroup._id,
      );
      const targetGroupIndex = newNoteGroups.findIndex(
        (g) => g._id === targetGroupId,
      );

      // Remove from source
      const [movedNote] = newNoteGroups[sourceGroupIndex].notes.splice(
        sourceNoteIndex,
        1,
      );

      // Add to empty target group
      newNoteGroups[targetGroupIndex].notes.push(movedNote);

      // Update UI immediately
      setNoteGroups(newNoteGroups);
      return;
    }

    // Find target note and group (existing logic)
    let targetGroup = null;
    let targetNoteIndex = -1;

    for (const group of noteGroups) {
      const noteIndex = group.notes?.findIndex((n) => n._id === over.id) ?? -1;
      if (noteIndex !== -1) {
        targetGroup = group;
        targetNoteIndex = noteIndex;
        break;
      }
    }

    if (!sourceGroup || !targetGroup) {
      return;
    }

    // Only update if moving between groups or different positions
    if (
      sourceGroup._id !== targetGroup._id ||
      sourceNoteIndex !== targetNoteIndex
    ) {
      const newNoteGroups = noteGroups.map((g) => ({
        ...g,
        notes: [...(g.notes || [])],
      }));

      const sourceGroupIndex = newNoteGroups.findIndex(
        (g) => g._id === sourceGroup._id,
      );
      const targetGroupIndex = newNoteGroups.findIndex(
        (g) => g._id === targetGroup._id,
      );

      // Remove from source
      const [movedNote] = newNoteGroups[sourceGroupIndex].notes.splice(
        sourceNoteIndex,
        1,
      );

      // Insert into target position
      newNoteGroups[targetGroupIndex].notes.splice(
        targetNoteIndex,
        0,
        movedNote,
      );

      // Update UI immediately
      setNoteGroups(newNoteGroups);
    }
  };

  const handleDragEnd = async (event) => {
    const { over } = event;

    // Clear the active note
    setActiveNote(null);

    if (!over) {
      return;
    }

    // The UI has already been updated by handleDragOver, so we need to use the CURRENT state
    // to generate the correct sort orders for the backend
    const updates = [];

    // Just use the current noteGroups state (which has already been updated by handleDragOver)
    // and generate sortOrder for all notes
    noteGroups.forEach((group) => {
      group.notes?.forEach((note, index) => {
        updates.push({
          _id: note._id,
          sortOrder: index,
          noteGroupId: group._id,
        });
      });
    });

    if (updates.length === 0) {
      return;
    }

    try {
      await fetcher.put("/api/notes/reorder", updates);
      // The backend will emit a socket event, but we've already updated the UI
    } catch (err) {
      console.error("Error reordering notes:", err);
      // If there's an error, refetch to get the correct state
      fetchNotes();
    }
  };

  const cancelRemoveDeletedNotes = () => {
    closeModal();
  };

  const removeDeletedNotes = async () => {
    try {
      await fetcher.delete(
        `/api/notes/completed?projectId=${currentProject?._id}`,
      );

      // Clear selection if a deleted note was selected
      if (selectedNote && !selectedNote.isActive) {
        setSelectedNote(null);
        setNoteTitle("");
        if (editor) {
          editor.commands.setContent("");
        }
      }

      // Toggle off "show deleted" since there are no more deleted notes to show
      setShowCompleted(false);

      await fetchNotes();
      closeModal();
    } catch (err) {
      console.error("Error removing deleted notes:", err);
    }
  };

  const closeRearrangeNoteGroups = () => {
    closeModal();
  };

  const handleAddNoteGroup = (e) => {
    openModal(
      e,
      "top-left",
      <AddNoteGroup
        createNoteGroup={createNoteGroup}
        cancelCreateNoteGroup={closeModal}
      />,
    );
  };

  const handleTextColorPicker = (e) => {
    openModal(
      e,
      "top-center",
      <TextColorPicker
        onSelectColor={(color) => {
          editor?.chain().focus().setColor(color).run();
          closeModal();
        }}
      />,
    );
  };

  const handleEmojiPicker = (e) => {
    openModal(
      e,
      "top-center",
      <EmojiPickerModal
        onSelectEmoji={(emoji) => {
          editor?.commands.insertContent(emoji);
          closeModal();
        }}
      />,
    );
  };

  const createNoteGroup = async (name, color) => {
    try {
      // Calculate the next sortOrder (highest current + 1)
      const maxSortOrder = noteGroups.reduce((max, group) => {
        return group.sortOrder > max ? group.sortOrder : max;
      }, 0);

      await fetcher.post("/api/noteGroups", {
        name,
        color,
        projectId: currentProject?._id,
        sortOrder: maxSortOrder + 1,
      });
      await fetchNotes();
      closeModal();
    } catch (err) {
      console.error("Error creating note group:", err);
    }
  };

  const handleEditNoteGroup = (e, noteGroup) => {
    e.stopPropagation();
    openModal(
      e,
      "top-left",
      <EditNoteGroup
        noteGroupData={noteGroup}
        updateNoteGroup={updateNoteGroup}
        cancelEditNoteGroup={closeModal}
      />,
    );
  };

  const updateNoteGroup = async (noteGroupId, name, color) => {
    try {
      await fetcher.put(`/api/noteGroups/${noteGroupId}`, {
        name,
        color,
      });
      await fetchNotes();
      closeModal();
    } catch (err) {
      console.error("Error updating note group:", err);
    }
  };

  const handleDeleteNoteGroup = (e, noteGroup) => {
    e.stopPropagation();

    openModal(
      e,
      "center-center",
      <RemoveNoteGroup
        removeNoteGroup={() => confirmDeleteNoteGroup(noteGroup)}
        cancelRemoveNoteGroup={closeModal}
        noteGroupName={noteGroup.name}
        noteCount={(noteGroup.notes || []).filter((n) => n.isActive).length}
      />,
    );
  };

  const confirmDeleteNoteGroup = async (noteGroup) => {
    try {
      await fetcher.put(`/api/noteGroups/${noteGroup._id}`, {
        isActive: false,
      });
      // If the current selected note belongs to this group, clear selection
      if (
        selectedNote &&
        noteGroup.notes?.some((note) => note._id === selectedNote._id)
      ) {
        setSelectedNote(null);
        setNoteTitle("");
        if (editor) {
          editor.commands.setContent("");
        }
      }
      closeModal();
      await fetchNotes();
    } catch (err) {
      console.error("Error deleting note group:", err);
    }
  };

  // Check if user should see onboarding pointer for note groups
  const isNotFiltering = !searchText.trim();
  const hasNoNoteGroups =
    currentProject &&
    noteGroups.length === 0 &&
    isNotFiltering &&
    isInitialLoadComplete;
  const showNoteGroupOnboarding = hasNoNoteGroups;

  return (
    <div className="notes-wrapper">
      <OnboardingPointer
        targetSelector=".add-notegroup-button"
        message={t("notes.createGroupOnboarding")}
        show={showNoteGroupOnboarding}
      />
      <ActionBar>
        {currentProject && (
          <>
            <ActionBarButton
              className="add-notegroup-button"
              buttonText={t("notes.actions.addNoteGroup")}
              icon={
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "20px" }}
                >
                  assignment_add
                </span>
              }
              onClick={handleAddNoteGroup}
            />
            <div className="action-bar-spacer" />
          </>
        )}
        <ActionBarButton
          buttonText={t("notes.actions.collapseAll")}
          icon={
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px" }}
            >
              collapse_content
            </span>
          }
          onClick={collapseAll}
        />
        <div className="action-bar-spacer" />
        <ActionBarButton
          buttonText={t("notes.actions.expandAll")}
          icon={
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px" }}
            >
              expand_content
            </span>
          }
          onClick={expandAll}
        />
        <div className="action-bar-spacer" />
        <ActionBarButton
          buttonText={t("notes.actions.rearrangeGroups")}
          icon={
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px" }}
            >
              move_down
            </span>
          }
          onClick={(e) =>
            openModal(
              e,
              "center-center",
              <RearrangeNoteGroups
                noteGroups={noteGroups}
                closeRearrangeNoteGroups={closeRearrangeNoteGroups}
                onSave={fetchNotes}
              />,
            )
          }
        />
        <div className="action-bar-spacer" />
        {showCompleted ? (
          <ActionBarButton
            buttonText={t("notes.actions.hideDeleted")}
            icon={
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "20px" }}
              >
                visibility_off
              </span>
            }
            onClick={() => setShowCompleted(false)}
          />
        ) : (
          <ActionBarButton
            buttonText={t("notes.actions.showDeleted")}
            icon={
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "20px" }}
              >
                visibility
              </span>
            }
            onClick={() => setShowCompleted(true)}
          />
        )}
        {showCompleted ? (
          <>
            <div className="action-bar-spacer" />
            <ActionBarButton
              buttonText={t("notes.actions.removeDeleted")}
              textColor="#e8697d"
              icon={
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "20px", color: "#e8697d" }}
                >
                  delete
                </span>
              }
              onClick={(e) =>
                openModal(
                  e,
                  "top-center",
                  <RemoveDeletedNotes
                    cancelRemoveDeletedNotes={cancelRemoveDeletedNotes}
                    removeDeletedNotes={removeDeletedNotes}
                  />,
                )
              }
            />
          </>
        ) : (
          <></>
        )}
        <div className="action-bar-spacer" />
        <ActionBarButton
          buttonText={t("notes.actions.downloadPdf")}
          icon={
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px" }}
            >
              picture_as_pdf
            </span>
          }
          onClick={generateNotePDF}
          disabled={!selectedNote || !selectedNote.isActive}
        />
      </ActionBar>
      <div className="notes">
        <div className="notes-content">
          <div className="notes-left-column">
            <div className="notes-column-header">{t("notes.noteGroups")}</div>
            <div className="notes-search">
              <FlowTextInput
                ref={searchInputRef}
                placeholder={t("notes.filterPlaceholder")}
                value={searchText}
                onChange={handleSearchChange}
                expandable={false}
                icon="search"
              />
            </div>
            {getFilteredNoteGroups().length === 0 ? (
              <EmptyStatePlaceholder
                topText={t("notes.empty.noNotes")}
                bottomText={
                  searchText.trim()
                    ? t("notes.empty.adjustSearch")
                    : t("notes.empty.createGroup")
                }
              />
            ) : (
              <>
                <DndContext
                  sensors={sensors}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                >
                  <div className="notes-list">
                    {getFilteredNoteGroups().map((noteGroup) => (
                      <div
                        key={noteGroup._id}
                        className="notegroup-wrapper"
                        style={{
                          "--notegroup-color": noteGroup.color,
                        }}
                      >
                        <div className="notegroup-header">
                          <div className="notegroup-title-container">
                            <span
                              className="material-symbols-outlined collapse-icon"
                              style={{ fontSize: "28px", cursor: "pointer" }}
                              onClick={() => toggleGroup(noteGroup.name)}
                            >
                              {collapsedGroups[noteGroup.name]
                                ? "keyboard_arrow_right"
                                : "keyboard_arrow_down"}
                            </span>
                            <span
                              className="notegroup-name"
                              onClick={() => toggleGroup(noteGroup.name)}
                              style={{ cursor: "pointer" }}
                            >
                              {noteGroup.name}
                            </span>
                            {collapsedGroups[noteGroup.name] && (
                              <span className="notegroup-count">
                                (
                                {t("notes.noteCount", {
                                  count: noteGroup.notes?.length || 0,
                                })}
                                )
                              </span>
                            )}
                            <span
                              className="material-symbols-outlined more-horiz"
                              style={{
                                fontSize: "24px",
                                marginLeft: "10px",
                                cursor: "pointer",
                              }}
                              onClick={(e) => handleEditNoteGroup(e, noteGroup)}
                            >
                              more_horiz
                            </span>
                            <span
                              className="material-symbols-outlined delete-icon"
                              style={{
                                fontSize: "24px",
                                marginLeft: "auto",
                                cursor: "pointer",
                                color: "white",
                              }}
                              onClick={(e) =>
                                handleDeleteNoteGroup(e, noteGroup)
                              }
                            >
                              delete
                            </span>
                          </div>
                        </div>
                        {!collapsedGroups[noteGroup.name] && (
                          <SortableContext
                            items={(noteGroup.notes || []).map(
                              (note) => note._id,
                            )}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="notegroup-container">
                              {noteGroup.notes &&
                              noteGroup.notes.length === 0 ? (
                                <EmptyNoteGroupDropZone
                                  noteGroupId={noteGroup._id}
                                />
                              ) : (
                                noteGroup.notes?.map((note) => (
                                  <SortableNoteItem
                                    key={note._id}
                                    note={note}
                                    selectedNote={selectedNote}
                                    handleNoteSelect={handleNoteSelect}
                                    handleDeleteNote={handleDeleteNote}
                                    handleRestoreNote={handleRestoreNote}
                                    linkedCount={
                                      linkedNoteCounts[note._id] || 0
                                    }
                                  />
                                ))
                              )}
                              <div className="add-note">
                                <input
                                  type="text"
                                  className="add-note-input"
                                  placeholder={t("notes.addNotePlaceholder")}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleAddNote(
                                        noteGroup.id,
                                        e.target.value,
                                      );
                                      e.target.value = "";
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </SortableContext>
                        )}
                      </div>
                    ))}
                  </div>
                  <DragOverlay>
                    {activeNote ? (
                      <div
                        className={`note-item ${!activeNote.isActive ? "deleted" : ""}`}
                        style={{
                          cursor: "grabbing",
                          opacity: 0.9,
                        }}
                      >
                        <div className="note-info">
                          <div className="note-title">{activeNote.title}</div>
                          <div className="note-meta">
                            {getTextPreview(activeNote.content || "", 50)}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              </>
            )}
          </div>
          <div className="note-editor-column">
            <div className="notes-column-header">{t("notes.note")}</div>
            {!selectedNote ? (
              <EmptyStatePlaceholder
                topText={t("notes.empty.selectNote")}
                bottomText={t("notes.empty.selectNoteHint")}
              />
            ) : (
              <>
                <div className="note-title-bar">
                  <FlowTextInput
                    className="note-title-flow-input"
                    placeholder={t("notes.titlePlaceholder")}
                    value={noteTitle}
                    onChange={(value) => setNoteTitle(value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleUpdateNote();
                      }
                    }}
                    expandable={false}
                    icon=""
                  />
                  <FlowButton variant="update" onClick={handleUpdateNote}>
                    <span style={{ position: "relative", top: "1px" }}>
                      {t("common.save")}
                    </span>
                    {autoUpdateEnabled && (
                      <span
                        className="material-symbols-outlined"
                        style={{
                          fontSize: "16px",
                          marginLeft: "4px",
                          verticalAlign: "middle",
                          animation:
                            isUploading || isAutosaving
                              ? "spin 1s ease-in-out infinite"
                              : "none",
                        }}
                      >
                        cached
                      </span>
                    )}
                  </FlowButton>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5em",
                      marginLeft: "1em",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={autoUpdateEnabled}
                      onChange={(e) => setAutoUpdateEnabled(e.target.checked)}
                    />
                    {t("notes.autoSave")}
                  </label>
                </div>
                <style>
                  {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
                </style>
                <div className="note-editor-wrapper">
                  <div className="note-editor-controls">
                    <button
                      className={`editor-btn ${activeFormats.bold ? "is-active" : ""}`}
                      onClick={() => editor?.chain().focus().toggleBold().run()}
                    >
                      <span className="material-symbols-outlined">
                        format_bold
                      </span>
                    </button>
                    <button
                      className={`editor-btn ${activeFormats.italic ? "is-active" : ""}`}
                      onClick={() =>
                        editor?.chain().focus().toggleItalic().run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_italic
                      </span>
                    </button>
                    <button
                      className={`editor-btn ${activeFormats.strike ? "is-active" : ""}`}
                      onClick={() =>
                        editor?.chain().focus().toggleStrike().run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_strikethrough
                      </span>
                    </button>
                    <span className="editor-separator" />
                    <button
                      className={`editor-btn ${activeFormats.h1 ? "is-active" : ""}`}
                      onClick={() =>
                        editor
                          ?.chain()
                          .focus()
                          .toggleHeading({ level: 1 })
                          .run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_h1
                      </span>
                    </button>
                    <button
                      className={`editor-btn ${activeFormats.h2 ? "is-active" : ""}`}
                      onClick={() =>
                        editor
                          ?.chain()
                          .focus()
                          .toggleHeading({ level: 2 })
                          .run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_h2
                      </span>
                    </button>
                    <button
                      className={`editor-btn ${activeFormats.h3 ? "is-active" : ""}`}
                      onClick={() =>
                        editor
                          ?.chain()
                          .focus()
                          .toggleHeading({ level: 3 })
                          .run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_h3
                      </span>
                    </button>
                    <span className="editor-separator" />
                    <button
                      className="editor-btn"
                      onClick={handleTextColorPicker}
                      title={t("notes.editor.textColor")}
                    >
                      <span className="material-symbols-outlined">
                        format_color_text
                      </span>
                    </button>
                    <button
                      className="editor-btn"
                      onClick={handleEmojiPicker}
                      title={t("notes.editor.insertEmoji")}
                    >
                      <span className="material-symbols-outlined">mood</span>
                    </button>
                    <span className="editor-separator" />
                    <button
                      className={`editor-btn ${activeFormats.bulletList ? "is-active" : ""}`}
                      onClick={() =>
                        editor?.chain().focus().toggleBulletList().run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_list_bulleted
                      </span>
                    </button>
                    <button
                      className={`editor-btn ${activeFormats.orderedList ? "is-active" : ""}`}
                      onClick={() =>
                        editor?.chain().focus().toggleOrderedList().run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_list_numbered
                      </span>
                    </button>
                    <button
                      className={`editor-btn ${activeFormats.taskList ? "is-active" : ""}`}
                      title={t("notes.editor.checklist")}
                      onClick={() =>
                        editor?.chain().focus().toggleTaskList().run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        checklist
                      </span>
                    </button>
                    <span className="editor-separator" />
                    <button
                      className={`editor-btn ${activeFormats.blockquote ? "is-active" : ""}`}
                      onClick={() =>
                        editor?.chain().focus().toggleBlockquote().run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_quote
                      </span>
                    </button>
                    <button
                      className={`editor-btn ${activeFormats.codeBlock ? "is-active" : ""}`}
                      onClick={() => {
                        if (!editor) return;

                        const { from, to } = editor.state.selection;
                        const selectedText = editor.state.doc.textBetween(
                          from,
                          to,
                          "\n",
                        );

                        // If we have selected text across multiple paragraphs
                        if (selectedText && selectedText.includes("\n")) {
                          editor
                            .chain()
                            .focus()
                            .deleteSelection()
                            .insertContent({
                              type: "codeBlock",
                              content: [{ type: "text", text: selectedText }],
                            })
                            .run();
                        } else {
                          // Normal toggle for single line or empty
                          editor.chain().focus().toggleCodeBlock().run();
                        }
                      }}
                    >
                      <span className="material-symbols-outlined">code</span>
                    </button>
                    <span className="editor-separator" />
                    <button
                      className="editor-btn"
                      onClick={handleImageButtonClick}
                      disabled={isUploadingImage}
                      title={t("notes.editor.uploadImage")}
                    >
                      <span className="material-symbols-outlined">
                        {isUploadingImage ? "hourglass_empty" : "image"}
                      </span>
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      style={{ display: "none" }}
                    />
                  </div>

                  <div
                    ref={editorScrollableRef}
                    className="note-editor-scrollable"
                    onClick={() => {
                      if (editor && !editor.isFocused) {
                        // Only focus at end if editor is not already focused
                        editor.commands.focus("end");
                      }
                    }}
                  >
                    <EditorContent
                      editor={editor}
                      className="note-content-area"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Modal ref={modalRef} />
    </div>
  );
}

export default Notes;
